import {
    componentName,
    componentClasses,
    eventMap,
    defaultProps,
    schema,
    views,
    width,
} from "../../../core/components/pagination/pagination";
import { prefix } from "../../../core/design-tokens/meta";
import {
    skipToFront,
    arrowTriLeft,
    arrowTriRight,
    skipToEnd,
} from "../../../core/js/helpers/built-in-icons";
import validateInitialization from "../../../core/js/helpers/validate-initialization";
import { renderState, dispatchEvent } from "../../../core/js/state-machine";
import { createElement, debounce, randomNumGenerator } from "../../../core/js/utilities";
import { matchers } from "../../../core/js/validate-props";
import Select from "../select/select";

const validateHTML = (element) => {
    const validElements = {
        summary: element.querySelector(`.${componentClasses.summary}`),
        perPageLabel: element.querySelector(`.${componentClasses.perPageLabel}`),
        perPageSelect: element.querySelector(`.${componentClasses.perPageSelect}`),
        perPageSelectField: element.querySelector(`.${componentClasses.perPageSelect} select`),
        perPageSelectFieldOptions:
            element.querySelectorAll(`.${componentClasses.perPageSelect} select option`).length > 0,
        range: element.querySelector(`.${componentClasses.range}`),
        rangeStart: element.querySelector(`.${componentClasses.rangeStart}`),
        rangeEnd: element.querySelector(`.${componentClasses.rangeEnd}`),
        rangeTotal: element.querySelector(`.${componentClasses.rangeTotal}`),
        rangeTotalLabel: element.querySelector(`.${componentClasses.rangeTotalLabel}`),
        firstPage: element.querySelector(`.${componentClasses.firstPage}`),
        prevPage: element.querySelector(`.${componentClasses.prevPage}`),
        prevPageLabel: element.querySelector(`.${componentClasses.prevPageLabel}`),
        pageRangeLabel: element.querySelector(`.${componentClasses.pageRangeLabel}`),
        pageRangeCurrent: element.querySelector(`.${componentClasses.pageRangeCurrent}`),
        pageRangeCurrentWrapper: element.querySelector(
            `.${componentClasses.pageRangeCurrentWrapper}`
        ),
        pageRangeTotalLabel: element.querySelector(`.${componentClasses.pageRangeTotalLabel}`),
        pageRangeTotal: element.querySelector(`.${componentClasses.pageRangeTotal}`),
        nextPage: element.querySelector(`.${componentClasses.nextPage}`),
        nextPageLabel: element.querySelector(`.${componentClasses.nextPageLabel}`),
        lastPage: element.querySelector(`.${componentClasses.lastPage}`),
    };

    return Object.values(validElements).every((element) => {
        return Boolean(element);
    })
        ? validElements
        : false;
};

/**
 * Initialize the Pagination and return the api
 * @param {HTMLElement} element
 * @param {defaultProps} props
 * @returns {{
 *   element: HTMLElement,
 *   nextPage: Function,
 *   prevPage: Function,
 *   firstPage: Function,
 *   lastPage: Function,
 *   updatePageSize: Function,
 *   goToPage: Function,
 *   eventMap: Object,
 * }}
 */
const Pagination = (element, props) => {
    const [success, mergedProps, validElements] = validateInitialization({
        componentName,
        element,
        props,
        validateHTML,
        defaultProps,
        schema,
    });

    if (!success) return;

    const injectSvgIcons = () => {
        validElements.firstPage.insertAdjacentHTML("afterbegin", skipToFront());
        validElements.lastPage.insertAdjacentHTML("afterbegin", skipToEnd());

        validElements.prevPage.insertAdjacentHTML(
            "afterbegin",
            arrowTriLeft({ class: "dds__button__icon--start" })
        );

        validElements.nextPage.insertAdjacentHTML(
            "beforeend",
            arrowTriRight({ class: "dds__button__icon--end" })
        );
    };

    const disposeSvgIcons = () => {
        const removeIcon = (icon) => icon.remove();
        validElements.firstPage.querySelectorAll("svg").forEach(removeIcon);
        validElements.lastPage.querySelectorAll("svg").forEach(removeIcon);
        validElements.prevPage.querySelectorAll("svg").forEach(removeIcon);
        validElements.nextPage.querySelectorAll("svg").forEach(removeIcon);
    };

    const updateItemsRange = () => {
        renderState(
            totalItems === 0 ? views.summary.itemsRange.hidden : views.summary.itemsRange.rest,
            validElements.range
        );
    };

    const updateItemsRangeStart = () => {
        validElements.rangeStart.textContent = getItemsRangeStart();
    };

    const updateItemsRangeEnd = () => {
        validElements.rangeEnd.textContent = getItemsRangeEnd();
    };

    const updateItemsRangeTotal = () => {
        validElements.rangeTotal.textContent = totalItems;
    };

    const updateCurrentPage = (value) => {
        validElements.pageRangeCurrent.value = value ? value : currentPage;
        if (
            !validElements.pageRangeCurrentWrapper.querySelector(
                `.${componentName}-aria-description`
            )
        ) {
            const id = `${componentName}-input-description-${randomNumGenerator()}`;
            const ariaPseudoElement = createElement("span", {
                id: id,
                text: mergedProps.ariaCurrentPageDescription.replace(
                    "{totalPages}",
                    getTotalPages()
                ),
                class: `${prefix}sr-only ${componentName}-aria-description`,
            });
            validElements.pageRangeCurrentWrapper.appendChild(ariaPseudoElement);
            validElements.pageRangeCurrent.setAttribute("aria-describedby", id);
        }
    };

    const updateTotalPages = () => {
        validElements.pageRangeTotal.textContent = getTotalPages();
    };

    const updateNavState = () => {
        renderState(hasPrevPage() ? views.nav.rest : views.nav.inactive, validElements.prevPage);
        renderState(hasPrevPage() ? views.nav.rest : views.nav.inactive, validElements.firstPage);
        renderState(hasNextPage() ? views.nav.rest : views.nav.inactive, validElements.nextPage);
        renderState(hasNextPage() ? views.nav.rest : views.nav.inactive, validElements.lastPage);
    };

    const getCurrentPage = () => {
        return currentPage;
    };

    const getPageSize = () => {
        return parseInt(validElements.perPageSelectField.value);
    };

    const getTotalPages = () => {
        return totalItems > 0 ? Math.ceil(totalItems / getPageSize()) : 1;
    };

    const getItemsRangeStart = () => {
        const pageSize = getPageSize();
        return totalItems > 0 ? currentPage * pageSize + 1 - pageSize : 0;
    };

    const getItemsRangeEnd = () => {
        const pageLastItem = currentPage * getPageSize();
        return totalItems > 0 ? (pageLastItem > totalItems ? totalItems : pageLastItem) : 0;
    };

    const getEventDetails = () => {
        return {
            currentPage: getCurrentPage(),
            itemsRangeStart: getItemsRangeStart(),
            itemsRangeEnd: getItemsRangeEnd(),
            totalPages: getTotalPages(),
            pageSize: getPageSize(),
        };
    };

    const isButtonInactive = (button) => {
        return button.getAttribute("aria-disabled") === "true";
    };

    const hasNextPage = () => {
        return currentPage < getTotalPages();
    };

    const hasPrevPage = () => {
        return currentPage > 1;
    };

    const getAvailablePage = (page) => {
        const totalPages = getTotalPages();

        if (page < 1) {
            console.warn(`${componentName} current page ${page} smaller than 1`);
            return 1;
        }
        if (page > totalPages) {
            console.warn(`${componentName} current page ${page} bigger than ${totalPages}`);
            return totalPages;
        }
        return page;
    };

    const setCurrentPage = (page) => {
        const availablePage = getAvailablePage(page);
        if (availablePage !== currentPage) {
            currentPage = availablePage;
            updateView();
            dispatchEvent(eventMap.pageChanged, element, getEventDetails());
        }
    };

    const setTotalItems = (total) => {
        totalItems = total;
        updateView();
    };

    const handlePageSizeChange = () => {
        updateView();
        dispatchEvent(eventMap.pageSizeChanged, element, getEventDetails());
    };

    /**
     * Pagination has a specific behavior based on component width (not screen width)
     * That is why we have specific width element
     * In each breakpoint the pagination will show/hide some of elements to fit the size wanted by design
     */
    const handlePaginationResize = debounce(() => {
        const offsetWidth = element.offsetWidth;

        if (offsetWidth < width[360]) {
            renderState(views.pagination.width360, element);
        } else if (offsetWidth < width[480]) {
            renderState(views.pagination.width480, element);
        } else if (offsetWidth < width[640]) {
            renderState(views.pagination.width640, element);
        } else if (offsetWidth < width[768]) {
            renderState(views.pagination.width768, element);
        } else if (offsetWidth < width[1024]) {
            renderState(views.pagination.width1024, element);
        } else {
            renderState(views.pagination.rest, element);
        }
    }, 100);

    const handleInputValueChange = (value) => {
        const intValue = parseInt(value);
        const availablePage = getAvailablePage(intValue);

        if (matchers.number(intValue) && availablePage !== currentPage) {
            setCurrentPage(intValue);
        } else {
            updateCurrentPage(currentPage);
        }
    };

    const handleCurrentPageKeydown = (e) => {
        if (e.keyCode === 13) handleInputValueChange(e.target.value);
    };

    const handleCurrentPageBlur = (e) => {
        handleInputValueChange(e.target.value);
    };

    const updateView = () => {
        // Changing totalItems or pageSize CAN change the current available page
        // In this case we verify if the available page is different than the current and set a new before updating the view
        const availablePage = getAvailablePage(currentPage);

        if (availablePage !== currentPage) {
            setCurrentPage(availablePage);
        } else {
            updateItemsRange();
            updateItemsRangeStart();
            updateItemsRangeEnd();
            updateItemsRangeTotal();
            updateTotalPages();
            updateCurrentPage();
            updateNavState();
        }
    };

    const firstPage = () => {
        if (!hasPrevPage() || isButtonInactive(validElements.firstPage)) return;
        setCurrentPage(1);
        dispatchEvent(eventMap.firstPageClicked, element, getEventDetails());
    };

    const prevPage = () => {
        if (!hasPrevPage() || isButtonInactive(validElements.prevPage)) return;
        setCurrentPage(currentPage - 1);
        dispatchEvent(eventMap.previousPageClicked, element, getEventDetails());
    };

    const nextPage = () => {
        if (!hasNextPage() || isButtonInactive(validElements.nextPage)) return;
        setCurrentPage(currentPage + 1);
        dispatchEvent(eventMap.nextPageClicked, element, getEventDetails());
    };

    const lastPage = () => {
        if (!hasNextPage() || isButtonInactive(validElements.lastPage)) return;
        setCurrentPage(getTotalPages());
        dispatchEvent(eventMap.lastPageClicked, element, getEventDetails());
    };

    const dispose = () => {
        selectInstance.dispose();
        validElements.perPageSelectField.removeEventListener("change", handlePageSizeChange);
        validElements.pageRangeCurrent.removeEventListener("keydown", handleCurrentPageKeydown);
        validElements.pageRangeCurrent.removeEventListener("blur", handleCurrentPageBlur);
        validElements.firstPage.removeEventListener("click", firstPage);
        validElements.prevPage.removeEventListener("click", prevPage);
        validElements.nextPage.removeEventListener("click", nextPage);
        validElements.lastPage.removeEventListener("click", lastPage);
        resizeObserver.disconnect();
        disposeSvgIcons();
        element[componentName] = null;
        element = null;
    };

    // Inject svg icons
    injectSvgIcons();

    // Init nested components
    const selectInstance = Select(validElements.perPageSelect);

    validElements.perPageSelectField.addEventListener("change", handlePageSizeChange);
    validElements.pageRangeCurrent.addEventListener("keydown", handleCurrentPageKeydown);
    validElements.pageRangeCurrent.addEventListener("blur", handleCurrentPageBlur);
    validElements.firstPage.addEventListener("click", firstPage);
    validElements.prevPage.addEventListener("click", prevPage);
    validElements.nextPage.addEventListener("click", nextPage);
    validElements.lastPage.addEventListener("click", lastPage);

    // States
    let totalItems = mergedProps.totalItems;
    let currentPage = getAvailablePage(mergedProps.currentPage);

    // Update view
    updateView();

    const resizeObserver = new ResizeObserver(handlePaginationResize);

    resizeObserver.observe(element);
    const api = {
        dispose,
        element,
        firstPage,
        getCurrentPage,
        getItemsRangeEnd,
        getItemsRangeStart,
        getPageSize,
        getTotalPages,
        lastPage,
        nextPage,
        prevPage,
        setCurrentPage,
        setTotalItems,
        eventMap,
    };

    element[componentName] = api;

    return api;
};

export default Pagination;
